import React, { Component } from 'react';
import Deploy from './deploy';

const FREE_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.co.uk', 'yahoo.co.in', 'yahoo.fr', 'yahoo.de',
  'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'hotmail.de',
  'outlook.com', 'live.com', 'live.co.uk', 'msn.com',
  'aol.com', 'icloud.com', 'me.com', 'mac.com',
  'protonmail.com', 'protonmail.ch', 'proton.me',
  'mail.com', 'zoho.com', 'yandex.com', 'yandex.ru',
  'gmx.com', 'gmx.net', 'gmx.de', 'gmx.us',
  'tutanota.com', 'tutamail.com', 'tuta.io',
  'fastmail.com', 'fastmail.fm',
  'hey.com', 'inbox.com', 'rediffmail.com',
  'qq.com', '163.com', '126.com',
  'comcast.net', 'att.net', 'verizon.net', 'sbcglobal.net', 'cox.net',
]);

function isFreeEmail(email) {
  const parts = email.toLowerCase().split('@');
  return parts.length !== 2 || FREE_DOMAINS.has(parts[1]);
}

export default class Content extends Component {
  constructor(props) {
    super(props);
    this.state = {
      annual: false,
      allowFreeEmails: false,

      // Unified email verification modal (Free + Pro)
      showEmailModal: false,
      emailTier: null,   // 'free' | 'pro'
      emailForm: { name: '', email: '', organization: '' },
      emailError: null,
      emailStatus: null, // null | 'sending' | 'sent' | 'error'

      // Token verification result (from ?token= URL param on page load)
      tokenStatus: null, // null | 'verifying' | 'verified_free' | 'subscribed' | 'error'
      tokenResult: null,

      // Contact Sales modal (Enterprise - unchanged)
      showModal: false,
      form: { name: '', email: '', organization: '', message: '' },
      status: null,
    };
  }

  componentDidMount() {
    fetch('/api/config')
      .then(r => r.ok ? r.json() : {})
      .then(cfg => { if (cfg.allow_free_emails) this.setState({ allowFreeEmails: true }); })
      .catch(() => {});

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const subscribed = params.get('subscribed');
    if (token) {
      window.history.replaceState({}, '', window.location.pathname);
      this.verifyToken(token);
    } else if (subscribed === '1') {
      window.history.replaceState({}, '', window.location.pathname);
      this.setState({ tokenStatus: 'subscribed' });
    }
  }

  // ── Email verification modal ──────────────────────────────────────────────

  handleEmailOpen = (tier) => this.setState({
    showEmailModal: true,
    emailTier: tier,
    emailForm: { name: '', email: '', organization: '' },
    emailError: null,
    emailStatus: null,
  });

  handleEmailClose = () => this.setState({ showEmailModal: false, emailError: null, emailStatus: null });

  handleEmailChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({ emailForm: { ...prev.emailForm, [name]: value }, emailError: null }));
  };

  handleEmailSubmit = async (e) => {
    e.preventDefault();
    const { emailForm, emailTier, annual, allowFreeEmails } = this.state;

    if (!allowFreeEmails && isFreeEmail(emailForm.email)) {
      this.setState({ emailError: 'Please use your corporate email address. Personal email providers are not accepted.' });
      return;
    }

    this.setState({ emailStatus: 'sending', emailError: null });
    try {
      const res = await fetch('/api/request-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: emailForm.name,
          email: emailForm.email,
          organization: emailForm.organization,
          tier: emailTier,
          billing: annual ? 'annual' : 'monthly',
        }),
      });
      if (res.status === 422) {
        this.setState({ emailStatus: null, emailError: 'Please use your corporate email address. Personal email providers are not accepted.' });
        return;
      }
      if (!res.ok) throw new Error();
      this.setState({ emailStatus: 'sent' });
    } catch {
      this.setState({ emailStatus: 'error' });
    }
  };

  // ── Token verification (called on page load when ?token= is present) ──────

  verifyToken = async (token) => {
    this.setState({ tokenStatus: 'verifying' });
    try {
      const res = await fetch('/api/verify-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.tier === 'pro' && data.url) {
        window.location.href = data.url;
        return;
      }
      this.setState({ tokenStatus: 'verified_free', tokenResult: data });
    } catch {
      this.setState({ tokenStatus: 'error' });
    }
  };

  // ── Contact Sales modal ───────────────────────────────────────────────────

  handleOpen  = () => this.setState({ showModal: true, status: null });
  handleClose = () => this.setState({ showModal: false, status: null });

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({ form: { ...prev.form, [name]: value } }));
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ status: 'sending' });
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.state.form),
      });
      if (!res.ok) throw new Error();
      this.setState({ status: 'success', form: { name: '', email: '', organization: '', message: '' } });
    } catch {
      this.setState({ status: 'error' });
    }
  };

  render() {
    const {
      annual, allowFreeEmails,
      showEmailModal, emailTier, emailForm, emailError, emailStatus,
      tokenStatus, tokenResult,
      showModal, form, status,
    } = this.state;

    const proPrice   = annual ? '$2,990' : '$299';
    const proPeriod  = annual ? '/ year' : '/ month';
    const proSavings = annual ? <span className="badge bg-success ms-2 fs-6">2 months free</span> : null;

    const deployServerLicense = tokenResult ? tokenResult.server_license : undefined;
    const deployAgentLicense  = tokenResult ? tokenResult.agent_license  : undefined;

    return (
      <>
      <Deploy server_license={deployServerLicense} agent_license={deployAgentLicense} />
      <main className="content">

        {/* ── Token verification overlay (shown on page load when ?token= present) ── */}
        {tokenStatus && (
          <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content text-light" style={{ backgroundColor: 'rgba(33, 37, 41, 0.97)', border: '1px solid rgba(255,255,255,0.15)' }}>
                {tokenStatus === 'verifying' && (
                  <div className="modal-body text-center py-5">
                    <div className="spinner-border text-info mb-3" role="status" />
                    <p className="mb-0 text-secondary">Verifying your email…</p>
                  </div>
                )}
                {tokenStatus === 'subscribed' && (
                  <div className="modal-body text-center py-4">
                    <p className="fs-4 mb-1 text-success">Subscription confirmed</p>
                    <p className="text-secondary mb-3">Your license keys are on their way - check your inbox.</p>
                    <button className="btn btn-outline-info" onClick={() => this.setState({ tokenStatus: null })}>Done</button>
                  </div>
                )}
                {tokenStatus === 'verified_free' && tokenResult && (
                  <div className="modal-body py-4">
                    <p className="fs-5 mb-1 text-success text-center">Email verified - your trial is ready</p>
                    <p className="text-secondary text-center small mb-4">
                      These keys have also been sent to <strong className="text-light">{tokenResult.email}</strong>.
                    </p>
                    <p className="text-secondary small mb-1">Organisation: <strong className="text-light">{tokenResult.organization}</strong></p>
                    <p className="text-secondary small mb-3">Expires: <strong className="text-light">{tokenResult.expires_at}</strong></p>
                    <div className="mb-3">
                      <label className="form-label small text-secondary mb-1">Server License</label>
                      <input readOnly className="form-control form-control-sm bg-black text-warning border-secondary font-monospace" value={tokenResult.server_license} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label small text-secondary mb-1">Agent License</label>
                      <input readOnly className="form-control form-control-sm bg-black text-warning border-secondary font-monospace" value={tokenResult.agent_license} />
                    </div>
                    <div className="text-center mt-3 d-flex gap-2 justify-content-center">
                      <button
                        className="btn btn-info"
                        data-bs-toggle="modal"
                        data-bs-target="#deployModal"
                        onClick={() => this.setState({ tokenStatus: null })}
                      >Deployment Guide</button>
                      <button className="btn btn-outline-secondary" onClick={() => this.setState({ tokenStatus: null })}>Done</button>
                    </div>
                  </div>
                )}
                {tokenStatus === 'error' && (
                  <div className="modal-body text-center py-4">
                    <p className="fs-5 mb-1 text-danger">Verification failed</p>
                    <p className="text-secondary mb-3">This link may have expired or already been used. Please request a new one.</p>
                    <button className="btn btn-outline-secondary" onClick={() => this.setState({ tokenStatus: null })}>Close</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Marketing content ──────────────────────────────────────────────── */}
        <section className="card mb-4 shadow-lg text-light" style={{ backgroundColor: 'rgba(33, 37, 41, 0.45)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <div className="card-body">
            <h1 className="fs-2">ForeignSix IR</h1>
            <h2 className="fs-4">Incident Response Orchestration - Remote, Rapid, Resilient</h2>
            <p>A modern, robust platform for cyber incident response orchestration - designed for scale and ease of use.</p>
            <p>Empower your incident responders, cybersecurity professionals, and digital forensic analysts to gather evidence from anywhere in the world, at any time, over a secure private network.</p>
            <p>Agents run on Windows, Linux, and macOS and deploy in minutes. <strong className="text-light">You deploy the IR server - on your own cloud, on-premises, or inside your private network.</strong> Evidence streams directly from agents to your server. ForeignSix never receives, relays, or has access to your data.</p>
            <p className="mb-0">Acquire over a direct, encrypted TLS tunnel and receive a court-ready Chain-of-Custody form when the job is done.</p>
          </div>
        </section>

        <section className="container-fluid p-0 mt-4">
          <article className="card mb-4 shadow-lg text-light" style={{ backgroundColor: 'rgba(33, 37, 41, 0.45)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <header className="card-header border-bottom border-secondary">
              <h3 className="fs-5 mb-0">The Future of Incident Response: Remote. Rapid. Resilient.</h3>
            </header>
            <div className="card-body">
              <strong>The "Old Way" of Digital Forensics and Incident Response needs an upgrade.</strong> In a world of remote work and global infrastructure, the "Old Way"-shipping hard drives, flying analysts across borders, and struggling with corporate firewalls-is a liability. While you wait for a physical disk to arrive, the attacker is already pivoting through your network.
              <br /><br />
              ForeignSix changes the game. We provide a "Forensic Command &amp; Control" platform that allows you to acquire bit-perfect disk images from any machine, anywhere on Earth, in minutes-not days.
              <br /><br />
              Agents reach your IR server over a single outbound, encrypted TLS connection - no VPN infrastructure to manage, no certificate authority to run, no port forwarding. Because the agent always dials out, it works through NATs and firewalls exactly like any outbound HTTPS request, while the server can still issue commands back down the same connection.
            </div>
          </article>

          <article className="card mb-4 shadow-lg text-light" style={{ backgroundColor: 'rgba(33, 37, 41, 0.45)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <header className="card-header border-bottom border-secondary">
              <h3 className="fs-5 mb-0">Scenario 1: The Midnight Ransomware "Pulse": A Case Study in Rapid Response</h3>
            </header>
            <div className="card-body">
              <strong>The Challenge:</strong> It's 2:00 AM in London. Your security team detects an active ransomware strain on a critical server in your Tokyo office. You need a full forensic image now to understand the entry point, but the Tokyo office is locked, and no one is on-site.
              <br /><br />
              <strong>The ForeignSix Solution: </strong>
              Zero-Touch Discovery: Your ForeignSix IR Server sees the Tokyo Agent is "Online" via its secure network heartbeat.
              Remote Triage: From your dashboard in London, you instantly see the server's NVMe drives and their health status.
              Stealth Acquisition: You trigger a forensic acquisition. The agent begins streaming the disk data, compressed to bypass the limited office bandwidth, directly to your IR server.
              <br /><br />
              <strong>The Result:</strong> By 4:00 AM, you have a verified, triple-hashed checksum (MD5/SHA1/SHA256) forensic image ready for analysis. You didn't need a plane ticket; you only needed ForeignSix IR.
            </div>
          </article>

          <article className="card mb-4 shadow-lg text-light" style={{ backgroundColor: 'rgba(33, 37, 41, 0.45)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <header className="card-header border-bottom border-secondary">
              <h3 className="fs-5 mb-0">Scenario 2: The "Air-Gapped" Corporate Audit</h3>
            </header>
            <div className="card-body">
              <strong>The Challenge: </strong> A high-security client requires an internal investigation into a suspicious laptop. They refuse to let any data leave their physical building, but your forensic team is working from a different city.
              <br /><br />
              <strong>The ForeignSix Solution: </strong>
              <ul>
                <li>Hybrid Deployment: You deploy a ForeignSix On-Prem Server inside the client's own network.</li>
                <li>Direct Tunnel: The Agent dials out to the Server over a private, encrypted TLS connection. No data ever touches the public internet.</li>
                <li>Triple-Hash Integrity: As the Agent images the drive, it calculates MD5, SHA1, and SHA256 hashes in real-time.</li>
              </ul>
              <br />
              <strong>The Result:</strong> The client is happy because their data stayed local. You are happy because you conducted a world-class forensic acquisition from your home office.
            </div>
          </article>

          <article className="card mb-4 shadow-lg text-light" style={{ backgroundColor: 'rgba(13, 110, 253, 0.08)', backdropFilter: 'blur(4px)', border: '1px solid rgba(13, 110, 253, 0.35)' }}>
            <header className="card-header border-bottom" style={{ borderColor: 'rgba(13,110,253,0.35)' }}>
              <h3 className="fs-5 mb-0 text-info">Your Infrastructure. Your Data. Zero Exposure.</h3>
            </header>
            <div className="card-body">
              ForeignSix is <strong>not</strong> a cloud service. When you subscribe, you receive a license key that unlocks the IR server binary - a self-contained binary you run on hardware you control. It can live in your AWS account, your Azure tenant, your on-premises data centre, or an air-gapped lab network. The choice is yours.
              <br /><br />
              Evidence data travels <em>directly</em> between your deployed agents and your server over an encrypted TLS 1.3 tunnel. There is no ForeignSix relay, no ForeignSix logging pipeline, and no ForeignSix storage. <strong>We are architecturally incapable of seeing your evidence.</strong>
              <br /><br />
              This architecture was a deliberate design decision, not a marketing claim. It means your forensic data is governed entirely by your own security controls, data-residency policies, and legal obligations - not ours.
            </div>
          </article>

          <article className="card mb-4 shadow-lg text-light" style={{ backgroundColor: 'rgba(33, 37, 41, 0.45)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <header className="card-header border-bottom border-secondary">
              <h3 className="fs-5 mb-0">The ForeignSix Guarantee: Verified Integrity</h3>
            </header>
            <div className="card-body">
              In digital forensics, the integrity of evidence is paramount. With ForeignSix, we don't just capture data; we ensure its authenticity with our triple-hash checksum (MD5/SHA1/SHA256) verification system.
              <br /><br />
              As the Agent images a drive, it calculates three cryptographic hashes in real-time. These hashes are then sent to the Server, which independently verifies them against the incoming data stream.
              At the end of every job, ForeignSix generates a Chain-of-Custody form. This isn't just a log; it's a cryptographically signed record of the "Ground Truth" - court-ready from the moment the job completes.
              <br /><br />
              "With ForeignSix, we didn't just get the data; we got the proof. The triple-hash checksum (MD5/SHA1/SHA256) verification gave our legal team the confidence that the evidence was untampered, from the suspect's desk to our server."
            </div>
          </article>
        </section>

        {/* ── Features ───────────────────────────────────────────────────────── */}
        <section id="features" className="mt-5 mb-5 pt-4 border-top border-secondary">
          <h2 className="fs-3 text-center mb-4">What ForeignSix IR Does</h2>
          <div className="row g-3">
            {[
              { icon: '#', title: 'Cross-Platform Agents', body: 'Deploy agents on Windows, Linux or macOS - self-contained binaries with no external dependencies.' },
              { icon: '#', title: 'Encrypted Reverse Tunnel', body: 'Agents dial out to your IR server over a single outbound TLS connection, which the server then uses to issue commands back - no VPN infrastructure, no certificate authority to run, no port forwarding, and it works through NATs and firewalls like any outbound HTTPS request.' },
              { icon: '#', title: 'Disk Space Visibility', body: 'Before triggering an acquisition, see the exact free and total disk space on every connected agent machine - no surprises mid-job.' },
              { icon: '#', title: 'Offline Agent Detection', body: 'The server continuously monitors agent heartbeats and immediately surfaces any agent that stops responding, so you always know the state of your deployed fleet.' },
              { icon: '#', title: 'Scheduled Acquisitions', body: 'Queue an evidence job with a delayed start time - the Agent runs it unattended at exactly the right moment.' },
              { icon: '#', title: 'Resumable Transfers', body: 'If a transfer is interrupted, it picks up where it left off. No wasted bandwidth, no restarting from zero.' },
              { icon: '#', title: 'Bottleneck Detection', body: 'Real-time pressure monitoring across disk, CPU and network surfaces the limiting resource so you can act on it immediately.' },
              { icon: '#', title: 'Triple-Hash Integrity (MD5/SHA1/SHA256)', body: 'MD5, SHA1 and SHA256 computed in real-time by the Agent and independently verified by the Server - no chance of silent corruption.' },
              { icon: '#', title: 'Chain-of-Custody form', body: 'Every completed acquisition generates a signed, court-ready Chain-of-Custody form documenting the full evidence chain from source to server.' },
              { icon: '#', title: 'Case Management', body: 'Organise evidence by Case ID and evidence name, with full analyst attribution tracked end-to-end.' },
            ].map(({ title, body }) => (
              <div key={title} className="col-md-6 col-xl-3">
                <div className="card h-100 text-light shadow" style={{ backgroundColor: 'rgba(33, 37, 41, 0.45)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="card-body">
                    <h3 className="fs-6 fw-semibold mb-2">{title}</h3>
                    <p className="text-secondary small mb-0">{body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Pricing ────────────────────────────────────────────────────────── */}
        <section id="get-started" className="mt-5 mb-5 pt-4 border-top border-secondary">
          <h2 className="fs-3 text-center mb-4">Pricing Plans</h2>
          <div className="d-flex justify-content-center mb-4">
            <div className="btn-group" role="group">
              <button className={`btn btn-sm ${!annual ? 'btn-info' : 'btn-outline-secondary'}`} onClick={() => this.setState({ annual: false })}>Monthly</button>
              <button className={`btn btn-sm ${annual ? 'btn-info' : 'btn-outline-secondary'}`}  onClick={() => this.setState({ annual: true })}>Annually</button>
            </div>
          </div>
          <div className="row g-4">

            {/* Free Tier */}
            <div className="col-lg-4">
              <div className="card h-100 shadow-lg text-light text-center p-3" style={{ backgroundColor: 'rgba(33, 37, 41, 0.45)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <div className="card-body d-flex flex-column">
                  <h3 className="card-title fs-4">Free Trial</h3>
                  <h4 className="fs-2 my-3">$0</h4>
                  <ul className="list-unstyled text-start mb-4 flex-grow-1">
                    <li className="mb-2">✓ 3 acquisitions total</li>
                    <li className="mb-2">✓ 1 persistent agent</li>
                    <li className="mb-2">✓ Single-user access</li>
                    <li className="mb-2">✓ Full hash verification</li>
                    <li className="mb-2">✓ Chain-of-Custody form</li>
                  </ul>
                  <button className="btn btn-outline-info w-100 mt-auto" onClick={() => this.handleEmailOpen('free')}>Start Free Trial</button>
                </div>
              </div>
            </div>

            {/* Pro Tier */}
            <div className="col-lg-4">
              <div className="card h-100 shadow-lg text-light text-center p-3" style={{ backgroundColor: 'rgba(13, 110, 253, 0.15)', backdropFilter: 'blur(4px)', border: '1px solid rgba(13, 110, 253, 0.5)' }}>
                <div className="card-body d-flex flex-column">
                  <h3 className="card-title fs-4">Pro</h3>
                  <h4 className="fs-2 my-3">{proPrice}<span className="fs-6 text-secondary"> {proPeriod}</span>{proSavings}</h4>
                  <ul className="list-unstyled text-start mb-4 flex-grow-1">
                    <li className="mb-2">✓ Unlimited acquisitions</li>
                    <li className="mb-2">✓ Up to 25 persistent agents</li>
                    <li className="mb-2">✓ 3 analyst seats</li>
                    <li className="mb-2">✓ Full reporting</li>
                    <li className="mb-2">✓ Chain-of-Custody form</li>
                    <li className="mb-2">✓ Priority support</li>
                  </ul>
                  <button className="btn btn-info w-100 mt-auto" onClick={() => this.handleEmailOpen('pro')}>Get Pro Access</button>
                </div>
              </div>
            </div>

            {/* Enterprise Tier */}
            <div className="col-lg-4">
              <div className="card h-100 shadow-lg text-light text-center p-3" style={{ backgroundColor: 'rgba(255, 193, 7, 0.14)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255, 193, 7, 0.65)', boxShadow: '0 0 18px rgba(255, 193, 7, 0.15)' }}>
                <div className="card-body d-flex flex-column">
                  <h3 className="card-title fs-4" style={{ color: '#ffc107' }}>Enterprise</h3>
                  <h4 className="fs-2 my-3">Starts at $1500/month</h4>
                  <ul className="list-unstyled text-start mb-4 flex-grow-1">
                    <li className="mb-2">✓ Unlimited agents &amp; seats</li>
                    <li className="mb-2">✓ SSO / SAML integration</li>
                    <li className="mb-2">✓ Audit logging &amp; API access</li>
                    <li className="mb-2">✓ Custom deployment assistance</li>
                    <li className="mb-2">✓ Dedicated 24/7 support</li>
                  </ul>
                  <button className="btn btn-outline-light w-100 mt-auto" onClick={this.handleOpen}>Get Enterprise Access</button>
                </div>
              </div>
            </div>

          </div>
          <div className="mt-3 p-3 rounded text-secondary small text-center" style={{ backgroundColor: 'rgba(33, 37, 41, 0.45)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <strong className="text-light">License Delivery</strong> - Free trial license keys are delivered by email immediately after email verification. Pro license keys are delivered by email immediately after successful payment. Enterprise licenses are issued directly by our team following contract completion.
          </div>
          <div className="mt-2 p-3 rounded text-secondary small text-center" style={{ backgroundColor: 'rgba(13, 110, 253, 0.06)', backdropFilter: 'blur(4px)', border: '1px solid rgba(13, 110, 253, 0.25)' }}>
            <strong className="text-info">All plans are self-hosted.</strong> You run the IR server on your own infrastructure - cloud, on-premises, or air-gapped. Your evidence data travels directly between your agents and your server. ForeignSix has no access to your data at any tier.
          </div>
        </section>

        {/* ── Email verification modal (Free + Pro) ─────────────────────────── */}
        {showEmailModal && (() => {
          const isFree     = emailTier === 'free';
          const title      = isFree ? 'Start your free trial' : 'Subscribe to ForeignSix Pro';
          const submitLabel = isFree ? 'Request trial access' : 'Continue to payment';
          const border     = isFree ? 'rgba(255,255,255,0.15)' : 'rgba(13,110,253,0.4)';
          return (
            <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content text-light" style={{ backgroundColor: 'rgba(33, 37, 41, 0.95)', border: `1px solid ${border}` }}>
                  <div className="modal-header border-secondary">
                    <h5 className="modal-title">{title}</h5>
                    <button type="button" className="btn-close btn-close-white" onClick={this.handleEmailClose} />
                  </div>
                  {emailStatus === 'sent' ? (
                    <div className="modal-body text-center py-4">
                      <p className="fs-5 mb-1">Check your inbox</p>
                      <p className="text-secondary">
                        We've sent a verification link to{' '}
                        <strong className="text-light">{emailForm.email}</strong>.
                      </p>
                      <p className="text-secondary small">
                        Click the link to {isFree ? 'activate your trial' : 'continue to payment'}.
                        The link expires in 1 hour.
                      </p>
                      <button className="btn btn-outline-info mt-2" onClick={this.handleEmailClose}>Done</button>
                    </div>
                  ) : (
                    <form onSubmit={this.handleEmailSubmit}>
                      <div className="modal-body">
                        {!isFree && (
                          <p className="text-secondary small mb-3">
                            We'll verify your corporate email before redirecting you to payment.
                            {annual ? ` You'll be billed ${proPrice}/year.` : ` You'll be billed ${proPrice}/month.`}
                          </p>
                        )}
                        <div className="mb-3">
                          <label className="form-label">Full Name</label>
                          <input
                            required
                            className="form-control bg-dark text-light border-secondary"
                            name="name"
                            value={emailForm.name}
                            onChange={this.handleEmailChange}
                            placeholder="Jane Smith"
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Corporate Email</label>
                          <input
                            required
                            type="email"
                            className={`form-control bg-dark text-light border-secondary${emailError ? ' is-invalid' : ''}`}
                            name="email"
                            value={emailForm.email}
                            onChange={this.handleEmailChange}
                            placeholder="jane@yourcompany.com"
                          />
                          {emailError
                            ? <div className="invalid-feedback">{emailError}</div>
                            : !allowFreeEmails && <div className="form-text text-secondary">Personal email providers (Gmail, Yahoo, etc.) are not accepted.</div>
                          }
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Organisation</label>
                          <input
                            required
                            className="form-control bg-dark text-light border-secondary"
                            name="organization"
                            value={emailForm.organization}
                            onChange={this.handleEmailChange}
                            placeholder="Acme Corp"
                          />
                        </div>
                        {emailStatus === 'error' && (
                          <p className="text-danger mb-0">Something went wrong - please try again.</p>
                        )}
                      </div>
                      <div className="modal-footer border-secondary">
                        <button type="button" className="btn btn-secondary" onClick={this.handleEmailClose}>Cancel</button>
                        <button
                          type="submit"
                          className={`btn ${isFree ? 'btn-outline-info' : 'btn-info'}`}
                          disabled={emailStatus === 'sending'}
                        >
                          {emailStatus === 'sending' ? 'Sending…' : submitLabel}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Contact Sales modal (Enterprise) ─────────────────────────────── */}
        {showModal && (
          <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content text-light" style={{ backgroundColor: 'rgba(33, 37, 41, 0.95)', border: '1px solid rgba(255,255,255,0.15)' }}>
                <div className="modal-header border-secondary">
                  <h5 className="modal-title">Contact Sales</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={this.handleClose} />
                </div>
                {status === 'success' ? (
                  <div className="modal-body text-center py-4">
                    <p className="fs-5 mb-1">Thanks - we'll be in touch soon.</p>
                    <button className="btn btn-outline-info mt-3" onClick={this.handleClose}>Close</button>
                  </div>
                ) : (
                  <form onSubmit={this.handleSubmit}>
                    <div className="modal-body">
                      <div className="mb-3">
                        <label className="form-label">Full Name</label>
                        <input required className="form-control bg-dark text-light border-secondary" name="name" value={form.name} onChange={this.handleChange} />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Company Email</label>
                        <input required type="email" className="form-control bg-dark text-light border-secondary" name="email" value={form.email} onChange={this.handleChange} />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Organisation</label>
                        <input className="form-control bg-dark text-light border-secondary" name="organization" value={form.organization} onChange={this.handleChange} />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">How can we help?</label>
                        <textarea required rows="4" className="form-control bg-dark text-light border-secondary" name="message" value={form.message} onChange={this.handleChange} />
                      </div>
                      {status === 'error' && <p className="text-danger mb-0">Something went wrong - please try again.</p>}
                    </div>
                    <div className="modal-footer border-secondary">
                      <button type="button" className="btn btn-secondary" onClick={this.handleClose}>Cancel</button>
                      <button type="submit" className="btn btn-info" disabled={status === 'sending'}>
                        {status === 'sending' ? 'Sending…' : 'Send Message'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Support ────────────────────────────────────────────────────────── */}
        <section id="support" className="mt-5 mb-5">
          <div className="card shadow-lg text-light text-center" style={{ backgroundColor: 'rgba(33, 37, 41, 0.45)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div className="card-body py-4">
              <h2 className="fs-3 mb-3">Support</h2>
              <p className="text-secondary mb-3">Need help getting started or deploying to your environment?</p>
              <button
                className="btn btn-outline-info me-3"
                data-bs-toggle="modal"
                data-bs-target="#deployModal"
              >Open Deployment Guide</button>
              <p className="text-secondary small mt-3 mb-0">For other enquiries, reach us at <strong className="text-light">support@foreignsix.com</strong></p>
            </div>
          </div>
        </section>

      </main>
      </>
    );
  }
}
